import { CSSProperties, FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, FormLabel, Typography } from '@mui/material';

import styles from './ArrivalTimePicker.module.scss';
import { PORTAL_POPOVER_Z } from '../../constants/modalZIndex';
import { ClockIcon } from '../../components/Icons/ClockIcon';
import { EditIcon } from '../../components/Icons/EditIcon';

const ITEM_HEIGHT = 36;
const VISIBLE_ROWS = 7;
const PICKER_HEIGHT = ITEM_HEIGHT * VISIBLE_ROWS;
const PADDING = (PICKER_HEIGHT - ITEM_HEIGHT) / 2;

const ROW_FONT_SIZES = [20, 18, 14, 12] as const;
const COLOR_ACTIVE = '#16191C';
const COLOR_INACTIVE = '#9096A2';

const getItemVisualStyle = (distanceFromCenter: number): CSSProperties => {
  const distance = Math.min(3, Math.round(Math.abs(distanceFromCenter)));
  const isActive = distance === 0;

  return {
    fontSize: `${ROW_FONT_SIZES[distance]}px`,
    color: isActive ? COLOR_ACTIVE : COLOR_INACTIVE,
    fontWeight: isActive ? 600 : 400,
  };
};

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS = ['AM', 'PM'] as const;
const LOOP_REPEAT_COUNT = 60;
const LOOP_RECENTER_MARGIN_CYCLES = 10;

type Period = (typeof PERIODS)[number];

type TimeParts = {
  hour12: number;
  minute: number;
  period: Period;
};

type ArrivalTimePickerProps = {
  required?: boolean;
  disabled?: boolean;
  name?: string;
  inputLabel?: string;
  placeholder?: string;
  error?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** z-index for portaled dropdown; must exceed parent modals on mobile */
  dropdownZIndex?: number;
};

const parseTime24 = (value: string): TimeParts | null => {
  if (!value || !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value)) return null;

  const [hourStr, minuteStr] = value.split(':');
  const hour24 = Number(hourStr);
  const minute = Number(minuteStr);
  const period: Period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return { hour12, minute, period };
};

const toTime24 = ({ hour12, minute, period }: TimeParts): string => {
  let hour24 = hour12 % 12;
  if (period === 'PM') hour24 += 12;

  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const formatDisplay12 = (value: string): string => {
  const parts = parseTime24(value);
  if (!parts) return '';

  const minuteLabel = String(parts.minute).padStart(2, '0');
  return `${parts.hour12}:${minuteLabel} ${parts.period}`;
};

const getDefaultParts = (): TimeParts => ({ hour12: 12, minute: 0, period: 'PM' });

type WheelColumnProps = {
  items: (string | number)[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  className?: string;
  formatItem?: (item: string | number) => string;
  loop?: boolean;
};

const WheelColumn: FC<WheelColumnProps> = ({
  items,
  selectedIndex,
  onSelect,
  className,
  formatItem,
  loop = false,
}) => {
  const cycleLength = items.length;
  const displayItems = loop
    ? Array.from({ length: LOOP_REPEAT_COUNT }, () => items).flat()
    : items;
  const middleOffset = loop ? Math.floor(LOOP_REPEAT_COUNT / 2) * cycleLength : 0;

  const columnRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgrammaticScroll = useRef(false);
  const [centerIndex, setCenterIndex] = useState(
    loop ? middleOffset + selectedIndex : selectedIndex
  );

  const toLogicalIndex = useCallback(
    (physicalIndex: number) => {
      if (!loop) return physicalIndex;
      return ((physicalIndex % cycleLength) + cycleLength) % cycleLength;
    },
    [cycleLength, loop]
  );

  const toPhysicalIndex = useCallback(
    (logicalIndex: number) => (loop ? middleOffset + logicalIndex : logicalIndex),
    [loop, middleOffset]
  );

  const syncCenterIndex = useCallback(() => {
    const column = columnRef.current;
    if (!column) return;
    setCenterIndex(column.scrollTop / ITEM_HEIGHT);
  }, []);

  const scrollToPhysicalIndex = useCallback((physicalIndex: number, smooth = false) => {
    const column = columnRef.current;
    if (!column) return;

    isProgrammaticScroll.current = true;
    column.scrollTo({
      top: physicalIndex * ITEM_HEIGHT,
      behavior: smooth ? 'smooth' : 'auto',
    });

    requestAnimationFrame(() => {
      isProgrammaticScroll.current = false;
    });
  }, []);

  const scrollToLogicalIndex = useCallback(
    (logicalIndex: number, smooth = false) => {
      scrollToPhysicalIndex(toPhysicalIndex(logicalIndex), smooth);
    },
    [scrollToPhysicalIndex, toPhysicalIndex]
  );

  const recenterIfNeeded = useCallback(
    (physicalIndex: number, logicalIndex: number) => {
      if (!loop) return physicalIndex;

      const lowerBound = cycleLength * LOOP_RECENTER_MARGIN_CYCLES;
      const upperBound = displayItems.length - cycleLength * LOOP_RECENTER_MARGIN_CYCLES - 1;

      if (physicalIndex < lowerBound || physicalIndex > upperBound) {
        const newPhysicalIndex = toPhysicalIndex(logicalIndex);
        scrollToPhysicalIndex(newPhysicalIndex, false);
        return newPhysicalIndex;
      }

      return physicalIndex;
    },
    [cycleLength, displayItems.length, loop, scrollToPhysicalIndex, toPhysicalIndex]
  );

  useLayoutEffect(() => {
    const physicalIndex = toPhysicalIndex(selectedIndex);
    scrollToPhysicalIndex(physicalIndex, false);
    setCenterIndex(physicalIndex);
  }, [selectedIndex, scrollToPhysicalIndex, toPhysicalIndex]);

  const handleScroll = () => {
    syncCenterIndex();

    if (isProgrammaticScroll.current) return;

    if (loop) {
      const column = columnRef.current;
      if (column) {
        const physicalIndex = Math.round(column.scrollTop / ITEM_HEIGHT);
        const logicalIndex = toLogicalIndex(physicalIndex);
        recenterIfNeeded(physicalIndex, logicalIndex);
      }
    }

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      const column = columnRef.current;
      if (!column) return;

      const physicalIndex = Math.round(column.scrollTop / ITEM_HEIGHT);
      const logicalIndex = loop
        ? toLogicalIndex(physicalIndex)
        : Math.max(0, Math.min(physicalIndex, items.length - 1));

      const targetPhysicalIndex = toPhysicalIndex(logicalIndex);
      const isNearEdge =
        loop &&
        (physicalIndex < cycleLength * LOOP_RECENTER_MARGIN_CYCLES ||
          physicalIndex > displayItems.length - cycleLength * LOOP_RECENTER_MARGIN_CYCLES - 1);

      if (targetPhysicalIndex !== physicalIndex) {
        scrollToPhysicalIndex(targetPhysicalIndex, !isNearEdge);
      }

      if (loop) {
        recenterIfNeeded(targetPhysicalIndex, logicalIndex);
      }

      if (logicalIndex !== selectedIndex) {
        onSelect(logicalIndex);
      }

      setCenterIndex(targetPhysicalIndex);
    }, 80);
  };

  const handleItemClick = (physicalIndex: number) => {
    const logicalIndex = toLogicalIndex(physicalIndex);
    onSelect(logicalIndex);
    scrollToLogicalIndex(logicalIndex, true);
  };

  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    },
    []
  );

  return (
    <Box
      ref={columnRef}
      className={`${styles.wheelColumn} ${className ?? ''}`}
      onScroll={handleScroll}
    >
      <Box
        className={styles.wheelColumnInner}
        style={{ paddingTop: PADDING, paddingBottom: PADDING }}
      >
        {displayItems.map((item, index) => (
          <Box
            key={`${item}-${index}`}
            className={styles.wheelItem}
            style={getItemVisualStyle(index - centerIndex)}
            onClick={() => handleItemClick(index)}
          >
            {formatItem ? formatItem(item) : item}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ArrivalTimePicker: FC<ArrivalTimePickerProps> = ({
  disabled = false,
  name = '',
  inputLabel = 'Time',
  placeholder = 'Select time',
  value = '',
  error = false,
  onChange,
  dropdownZIndex = PORTAL_POPOVER_Z,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [parts, setParts] = useState<TimeParts>(() => parseTime24(value) ?? getDefaultParts());
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: PICKER_HEIGHT + 16,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hourIndex = HOURS.indexOf(parts.hour12);
  const minuteIndex = parts.minute;
  const periodIndex = PERIODS.indexOf(parts.period);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const viewportPadding = 12;
    const isMobileViewport = window.innerWidth <= 768;
    const maxDropdownHeight = isMobileViewport
      ? Math.min(PICKER_HEIGHT + 16, Math.max(200, window.innerHeight * 0.42))
      : PICKER_HEIGHT + 16;

    const safeTop = viewportPadding;
    const safeBottom = window.innerHeight - viewportPadding;
    const spaceBelow = safeBottom - rect.bottom - gap;
    const spaceAbove = rect.top - gap - safeTop;
    const openAbove = spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;

    let top = openAbove ? rect.top - maxDropdownHeight - gap : rect.bottom + gap;
    top = Math.max(safeTop, Math.min(top, safeBottom - maxDropdownHeight));

    const dropdownWidth = Math.max(rect.width, 260);
    const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;
    const left = Math.max(viewportPadding, Math.min(rect.left, maxLeft));

    setDropdownPosition({
      top,
      left,
      width: rect.width,
      maxHeight: maxDropdownHeight,
    });
  }, []);

  useEffect(() => {
    if (value) {
      const parsed = parseTime24(value);
      if (parsed) setParts(parsed);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();
    triggerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const emitChange = useCallback(
    (nextParts: TimeParts) => {
      if (!onChange) return;

      const syntheticEvent = {
        target: {
          name,
          value: toTime24(nextParts),
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    },
    [name, onChange]
  );

  const updateParts = (updater: (prev: TimeParts) => TimeParts) => {
    setParts((prev) => {
      const next = updater(prev);
      emitChange(next);
      return next;
    });
  };

  const displayValue = value ? formatDisplay12(value) : '';

  const dropdown = isOpen
    ? createPortal(
        <Box
          ref={dropdownRef}
          className={styles.dropdown}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: Math.max(dropdownPosition.width, 260),
            maxHeight: dropdownPosition.maxHeight,
            zIndex: dropdownZIndex,
          }}
          role="dialog"
          aria-label="Time picker"
        >
          <Box className={styles.picker}>
            <Box className={styles.selectionHighlight} />
            <WheelColumn
              items={HOURS}
              selectedIndex={hourIndex >= 0 ? hourIndex : 0}
              loop
              onSelect={(index) => updateParts((prev) => ({ ...prev, hour12: HOURS[index] }))}
            />
            <WheelColumn
              items={MINUTES}
              selectedIndex={minuteIndex}
              formatItem={(item) => String(item).padStart(2, '0')}
              onSelect={(index) => updateParts((prev) => ({ ...prev, minute: index }))}
            />
            <WheelColumn
              items={[...PERIODS]}
              selectedIndex={periodIndex >= 0 ? periodIndex : 0}
              onSelect={(index) =>
                updateParts((prev) => ({ ...prev, period: PERIODS[index] as Period }))
              }
              className={styles.periodColumn}
            />
          </Box>
        </Box>,
        document.body
      )
    : null;

  return (
    <Box
      ref={containerRef}
      className={styles.container}
    >
      <FormLabel className={styles.label}>
        <Typography variant="h11Semibold">{inputLabel}</Typography>
      </FormLabel>

      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${isOpen ? styles.open : ''} ${error ? styles.error : ''}`}
        onClick={() => {
          if (disabled) return;
          if (!isOpen) updatePosition();
          setIsOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className={styles.triggerIcon}>
          <ClockIcon size={20} />
        </span>
        <span className={`${styles.triggerValue} ${!displayValue ? styles.placeholder : ''}`}>
          {displayValue || placeholder}
        </span>
        <span className={styles.editIcon}>
          <EditIcon
            size={18}
            color="#5E6A81"
          />
        </span>
      </button>

      {dropdown}
    </Box>
  );
};

export default ArrivalTimePicker;
